import React, { useMemo } from 'react';
import { Button, Checkbox, Input, Tooltip, Typography } from 'antd';
import { DeleteOutlined, QuestionCircleOutlined, PlusOutlined } from '@ant-design/icons';
import LabelReplacementSelect from '@/features/datasets/components/LabelReplacementSelect';
import LabelMappingColumnsHeader, { REJECTION_COLUMN_WIDTH } from '@/features/datasets/components/LabelMappingColumnsHeader';

const { Text } = Typography;

export default function AIGeneratedLabelsManager({
    labels = [], // [{ id, name, replaceWith, isNew }]
    onChangeNewLabelName,
    onChangeReplaceWith,
    onDeleteLabel,
    onAddLabel,
    availableLabels = [],
    title = 'Generate labels with AI',
    /** Single-label classification fallback "Other" column (mutually exclusive). */
    showRejectionColumn = false,
    rejectionLabelName = null,
    onSetRejectionLabel,
}) {
    const rows = useMemo(() => (Array.isArray(labels) ? labels : []), [labels]);

    return (
        <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Text style={{ fontWeight: 600, color: '#222', fontSize: 14, margin: 0 }}>
                    {title}
                </Text>
                <Tooltip title="Review and edit suggested labels. You can also map them to existing labels.">
                    <QuestionCircleOutlined style={{ color: '#878787', fontSize: 14, cursor: 'help' }} />
                </Tooltip>
            </div>

            {rows.length > 0 && (
                <LabelMappingColumnsHeader
                    firstLabel="Label generated"
                    firstTooltip="This label was generated by AI"
                    secondLabel="Replace with Label"
                    secondTooltip="Choose an existing label to replace this one, or create a new label"
                    showRejectionColumn={showRejectionColumn}
                    gap={16}
                />
            )}

            {rows.map((row) => {
                const isNew = !!row?.isNew;
                const effectiveLabel = isNew
                    ? (row.name || '')
                    : (row.replaceWith || row.name || '');
                return (
                <div
                    key={row.id}
                    style={{
                        display: 'flex',
                        gap: 16,
                        alignItems: 'flex-start',
                        marginTop: 16,
                    }}
                >
                    <div style={{ flex: 1 }}>
                        <Input
                            value={row.name}
                            placeholder={isNew ? 'enter label name' : 'Label'}
                            disabled={!isNew}
                            onChange={isNew ? (e) => onChangeNewLabelName?.(row.id, e.target.value) : undefined}
                            suffix={(
                                <Tooltip title="Delete label">
                                    <DeleteOutlined onClick={() => onDeleteLabel?.(row.id)} style={{ cursor: 'pointer' }} />
                                </Tooltip>
                            )}
                        />
                    </div>

                    {!isNew ? (
                        <div style={{ flex: 1 }}>
                            <LabelReplacementSelect
                                labelType="Label"
                                showHeader={false}
                                value={row.replaceWith || row.name}
                                onChange={(v) => onChangeReplaceWith?.(row.id, v === 'none' ? null : v)}
                                availableLabels={availableLabels}
                                allowNoneOption={false}
                                onCreateNewLabel={(newLabel) => onChangeReplaceWith?.(row.id, newLabel)}
                            />
                        </div>
                    ) : (
                        <div style={{ flex: 1 }} />
                    )}

                    {showRejectionColumn && (
                        <div style={{ flex: `0 0 ${REJECTION_COLUMN_WIDTH}px`, minWidth: REJECTION_COLUMN_WIDTH, height: 32, display: 'flex', alignItems: 'center' }}>
                            <Checkbox
                                checked={rejectionLabelName != null && rejectionLabelName === effectiveLabel}
                                disabled={!effectiveLabel}
                                onChange={(e) => {
                                    onSetRejectionLabel?.(e.target.checked ? effectiveLabel : null);
                                }}
                            />
                        </div>
                    )}
                </div>
                );
            })}

            <div style={{ marginTop: 16 }}>
                <Button
                    type="link"
                    icon={<PlusOutlined />}
                    onClick={() => onAddLabel?.()}
                    style={{ padding: 0, height: 'auto', color: '#1777ff' }}
                >
                    Add label
                </Button>
            </div>
        </div>
    );
}

